import type {
  DeviceNotice,
  DeviceNotifier,
  DevicePermission,
} from '@/application/ports/DeviceNotifier';

/**
 * The browser's Notification API, through the service worker where there is
 * one: an installed app on a phone shows a notification only that way.
 *
 * Every call is guarded. A browser without the API, an embedded view, and
 * jsdom all answer `unsupported` and show nothing, and a failure to show is
 * swallowed: a notification that could not be drawn is still in the centre.
 */
export class BrowserDeviceNotifier implements DeviceNotifier {
  permission(): DevicePermission {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') return 'unsupported';
    return window.Notification.permission;
  }

  async request(): Promise<DevicePermission> {
    if (this.permission() === 'unsupported') return 'unsupported';
    try {
      return await window.Notification.requestPermission();
    } catch {
      return this.permission();
    }
  }

  show(notice: DeviceNotice): void {
    if (this.permission() !== 'granted') return;
    const options: NotificationOptions = { body: notice.body, tag: notice.id };

    const direct = () => {
      try {
        new window.Notification(notice.title, options);
      } catch {
        // A phone refuses the constructor outside a service worker: nothing to do.
      }
    };

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistration()
        .then((registration) =>
          registration === undefined ? direct() : registration.showNotification(notice.title, options),
        )
        .catch(direct);
      return;
    }
    direct();
  }
}
